<script lang="ts">
  import {Icon} from '@/components';
  import Dialog from '@/components/dialog';
  import {TimeInput} from '@/components/input';
  import {VideoTimelinePreview} from '@/types/upload';
  import {createEventDispatcher, onMount} from 'svelte';
  import {
    formatTime,
    generateLocalVideoTimeline,
    getTimeConstraints,
    secondsToTimeComponents,
    videoToObjectUrl,
    type TimeComponents
  } from './TrimVideoDialog';
  import {TrimVideoEvent} from './types';

  export let video: File | string;
  export let timeline: VideoTimelinePreview[] = [];

  const dispatch = createEventDispatcher<{
    submit: TrimVideoEvent;
    close: void;
  }>();

  let videoElement: HTMLVideoElement;
  let previewVideoElement: HTMLVideoElement;
  let videoUrl: string; // Much faster to load from object url
  let originalVideoUrl: string; // Used for quick preview and streamed
  let duration = 0;
  let currentTime = 0;
  let trimStart = 0;
  let trimEnd = 0;
  let isPlaying = false;
  let isDraggingStart = false;
  let isDraggingEnd = false;
  let isDraggingTime = false;
  let isTrimModified = false;
  let isGeneratingTimeline = false;
  let generateTimelineProgress = 0;
  let timelineContainer: HTMLElement;
  let tooltipTime = 0;
  let showTooltip = false;
  let tooltipPosition = 0;
  let errorMessage = '';

  let startTime: TimeComponents = {hours: '00', minutes: '00', seconds: '00'};
  let endTime: TimeComponents = {hours: '00', minutes: '00', seconds: '00'};
  let durationTime: TimeComponents = {hours: '00', minutes: '00', seconds: '00'};

  $: {
    startTime = secondsToTimeComponents(trimStart);
    endTime = secondsToTimeComponents(trimEnd);
    durationTime = secondsToTimeComponents(duration);
  }

  $: isTrimModified = trimStart > 0 || trimEnd < Math.floor(duration);

  // Reactive calculation for time labels
  let timeLabels: {time: number; label: string; position: number}[] = [];

  $: {
    if (duration > 0) {
      const numIntervals = 3;
      const interval = duration / numIntervals;
      timeLabels = [];
      timeLabels.push({
        time: 0,
        label: formatTime(0),
        position: 0
      });

      timeLabels.push(
        ...Array.from({length: numIntervals - 1}, (_, i) => {
          // Calculate time for intermediate points (i=0 -> 1*interval, i=1 -> 2*interval, etc.)
          const time = (i + 1) * interval;
          return {
            time,
            label: formatTime(time),
            position: (time / duration) * 100
          };
        })
      );

      timeLabels.push({
        time: duration,
        label: formatTime(duration),
        position: 100
      });
    } else {
      timeLabels = [];
    }
  }

  onMount(async () => {
    isGeneratingTimeline = true;
    if (typeof video === `string`) originalVideoUrl = video;
    videoUrl = await videoToObjectUrl(video, progress => {
      generateTimelineProgress = progress;
    }).catch(() => {
      errorMessage = 'Timeline preview unavailable';

      setTimeout(() => {
        errorMessage = '';
        isGeneratingTimeline = false;
      }, 3000);

      return '';
    });

    return () => {
      if (videoUrl) {
        URL.revokeObjectURL(videoUrl);
      }
    };
  });

  const getLocalTimeline = async () => {
    if (timeline.length > 0 && !videoUrl) return;
    timeline = await generateLocalVideoTimeline(previewVideoElement, progress => {
      generateTimelineProgress = progress;
    }).finally(() => {
      isGeneratingTimeline = false;
    });
  };

  const handleTimeUpdate = () => {
    if (isDraggingTime) return;
    currentTime = videoElement.currentTime;

    // Check if we've reached or exceeded trimEnd
    if (trimEnd > 0 && currentTime >= trimEnd) {
      videoElement.currentTime = trimStart;
      if (isPlaying) {
        // Need to call play() again because some browsers pause at the end
        videoElement.play().catch(() => {
          // Ignore play() errors
        });
      }
    }
  };

  const handleVideoEnded = () => {
    if (trimEnd < duration) {
      videoElement.currentTime = trimStart;
      if (isPlaying) {
        videoElement.play().catch(() => {
          // Ignore play() errors
        });
      }
    }
  };

  const handleLoadedMetadata = () => {
    videoElement.currentTime = trimStart;
    duration = videoElement.duration;
    trimEnd = duration;
  };

  const handlePreviewVideoLoadedMetadata = () => {
    getLocalTimeline();
  };

  const handlePlayPause = () => {
    if (isPlaying) videoElement.pause();
    else videoElement.play();
    isPlaying = !isPlaying;
  };

  const calculateTimeFromEvent = (clientX: number) => {
    if (!timelineContainer) return 0;
    const rect = timelineContainer.getBoundingClientRect();
    const pos = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return Math.max(0, Math.min(duration, pos * duration));
  };

  const handleWindowMouseMove = (e: MouseEvent) => {
    if (!isDraggingStart && !isDraggingEnd && !isDraggingTime) return;

    const newTime = calculateTimeFromEvent(e.clientX);

    if (isDraggingStart) {
      const maxStart = trimEnd - 1;
      trimStart = Math.max(0, Math.min(maxStart, newTime));
      videoElement.currentTime = trimStart;
    } else if (isDraggingEnd) {
      const minEnd = trimStart + 1;
      trimEnd = Math.max(minEnd, Math.min(duration, newTime));
      videoElement.currentTime = trimEnd - 1;
    } else if (isDraggingTime && newTime >= trimStart && newTime <= trimEnd) {
      currentTime = newTime;
      videoElement.currentTime = newTime;
    }
  };

  const handleDragStart = (e: MouseEvent, type: 'start' | 'end' | 'time') => {
    e.preventDefault();
    if (type === 'start') isDraggingStart = true;
    else if (type === 'end') isDraggingEnd = true;
    else isDraggingTime = true;
    document.body.style.userSelect = 'none';
    videoElement.pause();
  };

  const handleDragEnd = () => {
    if (isDraggingStart || isDraggingEnd) videoElement.play();
    isDraggingStart = false;
    isDraggingEnd = false;
    isDraggingTime = false;
    document.body.style.userSelect = '';
  };

  const handleTimeInput = (e: CustomEvent<TimeComponents>, isStartTime: boolean) => {
    // Convert current time components to seconds
    const timeToSeconds = (time: TimeComponents) => {
      return parseInt(time.hours) * 3600 + parseInt(time.minutes) * 60 + parseInt(time.seconds);
    };

    const newTimeInSeconds = timeToSeconds(e.detail);

    isStartTime ? (trimStart = newTimeInSeconds) : (trimEnd = newTimeInSeconds);
    if (currentTime < trimStart || currentTime > trimEnd) {
      currentTime = trimStart;
      videoElement.currentTime = trimStart;
    }
  };

  const handleTimelineClick = (e: MouseEvent) => {
    const newTime = calculateTimeFromEvent(e.clientX);
    if (newTime >= trimStart && newTime <= trimEnd) {
      currentTime = newTime;
      videoElement.currentTime = newTime;
    }
  };

  const handleClose = () => {
    dispatch('close');
  };

  const onSubmit = (e: SubmitEvent) => {
    const submitButton = e.submitter as HTMLButtonElement;
    const shouldTrim = submitButton.dataset.action === 'trim';

    if (shouldTrim) {
      const start = Math.floor(trimStart);
      // For Local files, end represents duration (Handbrake)
      // For URLs, end represents actual end time (YTDL)
      const end =
        video instanceof File
          ? Math.floor(trimEnd - trimStart) // Duration for Handbrake
          : start + Math.floor(trimEnd - trimStart); // End time for YTDL

      dispatch('submit', {start, end});
    } else {
      dispatch('submit');
    }
  };

  const handleTimelineMouseMove = (e: MouseEvent) => {
    const time = calculateTimeFromEvent(e.clientX);
    tooltipTime = time;
    const rect = timelineContainer.getBoundingClientRect();
    const isOutOfBounds = tooltipPosition < 0 || tooltipPosition > rect.width;
    tooltipPosition = e.clientX - rect.left;

    if (isDraggingStart || isDraggingEnd || isOutOfBounds) showTooltip = false;
    else showTooltip = true;
  };

  const handleTimelineMouseLeave = () => {
    showTooltip = false;
  };
</script>

<!-- So that it resets the state when the dialog is opened -->

<Dialog isDialogOpen={true}>
  <div class="flex h-full w-full items-end justify-center lg:items-center">
    <form
      on:submit|preventDefault={onSubmit}
      class="relative h-auto max-h-[90vh] w-full min-w-[280px] overflow-y-auto rounded-2xl border border-zinc-400 bg-white p-12 shadow-lg dark:border-zinc-600 dark:bg-zinc-700 lg:max-w-[871px]"
    >
      <div class="mb-6">
        <h1 class="mb-4 text-2xl font-bold">Trim Your Video</h1>
        <button
          on:click|preventDefault={handleClose}
          class="absolute right-4 top-4 flex items-center rounded-full px-3 py-2 hover:bg-zinc-100"
        >
          <i class="fa-solid fa-xmark text-lg text-black" aria-hidden="true"></i>
        </button>

        <p class="text-sm text-sermon-base-300">
          Trim your video on upload to focus on the sermon and improve content accuracy. Our AI works best when you set
          the exact start and end times—plus, you'll only be charged for the trimmed length, saving you credits.
        </p>
      </div>
      <div class="relative flex flex-col gap-y-4">
        {#if duration === 0}
          <div class="absolute inset-0 flex items-center justify-center">
            <div class="flex items-center gap-x-2">
              <Icon name="Spinner" className="w-4 h-4 animate-spin text-sermon-base-300" />
              <span class="text-sermon-base-300">Loading video...</span>
            </div>
          </div>
        {/if}
        <!-- Video Player -->
        <div class="flex items-center justify-center">
          <video
            bind:this={videoElement}
            autoplay
            src={originalVideoUrl && !videoUrl ? originalVideoUrl : videoUrl}
            class="mb-4 aspect-video w-[395px]"
            on:loadedmetadata={handleLoadedMetadata}
            on:timeupdate={handleTimeUpdate}
            on:ended={handleVideoEnded}
            on:play={() => (isPlaying = true)}
            on:pause={() => (isPlaying = false)}
          >
            <track kind="captions" />
          </video>

          <!-- Use for timeline generation -->
          {#if isGeneratingTimeline}
            <video
              bind:this={previewVideoElement}
              src={videoUrl}
              class="hidden aspect-video w-[395px]"
              on:loadedmetadata={handlePreviewVideoLoadedMetadata}
            >
              <track kind="captions" />
            </video>
          {/if}
        </div>

        <!-- Video Controls -->
        {#if duration > 0}
          <div id="video-controls">
            <div id="video-timeline-container" class="relative">
              <div id="video-timeline-labels" class="relative mb-2 ml-[49px] mr-[16px] h-3 w-[calc(100%-49px-16px)]">
                {#if showTooltip}
                  <div
                    class="pointer-events-none absolute z-10 -mt-8 rounded bg-sermon-base-600 px-2 py-1 text-xs text-white before:absolute before:left-1/2 before:top-[100%] before:-translate-x-1/2 before:border-4 before:border-transparent before:border-t-sermon-base-600 before:content-['']"
                    style="left: {tooltipPosition}px; transform: translateX(-50%)"
                  >
                    {formatTime(tooltipTime)}
                  </div>
                {/if}
                <div>
                  {#each timeLabels as label, index (label.time)}
                    {#if index > 0}
                      <!-- Three separators between each label -->
                      <div
                        class="absolute bottom-0 h-2 w-[1px] -translate-x-1/2 transform bg-sermon-base-300 dark:bg-zinc-200"
                        style="left: {timeLabels[index - 1].position +
                          (label.position - timeLabels[index - 1].position) * 0.25}%"
                      />
                      <div
                        class="absolute bottom-0 h-2 w-[1px] -translate-x-1/2 transform bg-sermon-base-300 dark:bg-zinc-200"
                        style="left: {timeLabels[index - 1].position +
                          (label.position - timeLabels[index - 1].position) * 0.5}%"
                      />
                      <div
                        class="absolute bottom-0 h-2 w-[1px] -translate-x-1/2 transform bg-sermon-base-300 dark:bg-zinc-200"
                        style="left: {timeLabels[index - 1].position +
                          (label.position - timeLabels[index - 1].position) * 0.75}%"
                      />
                    {/if}
                    <span
                      class="absolute -translate-x-1/2 transform text-xs text-sermon-base-300 dark:text-zinc-200"
                      style="left: {label.position}%"
                    >
                      {label.label}
                    </span>
                  {/each}
                </div>
              </div>

              <div id="video-timeline" class="relative flex h-[52px] w-[calc(100%-16px)] rounded-l-xl bg-gray-400">
                <button
                  type="button"
                  class={`relative z-[2] flex h-full w-[33px] items-center justify-center rounded-l-xl text-white
                  ${isPlaying ? 'bg-sermon-pink' : 'bg-gray-400'}`}
                  on:click={handlePlayPause}
                  aria-label={isPlaying ? 'Pause video' : 'Play video'}
                >
                  {#if isPlaying}
                    <svg class="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                      <rect x="6" y="4" width="4" height="16" />
                      <rect x="14" y="4" width="4" height="16" />
                    </svg>
                  {:else}
                    <svg class="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  {/if}
                </button>

                <!-- Left Filler -->
                <div class={`-z-[1] flex h-full w-4 rounded-l-xl border-gray-500 bg-gray-400`} />

                <!-- Trim Timeline -->
                <!-- svelte-ignore a11y-click-events-have-key-events -->
                <div
                  id="video-trim-timeline-container"
                  bind:this={timelineContainer}
                  class="relative z-[1] h-full flex-1"
                  on:click={handleTimelineClick}
                  on:mousemove={handleTimelineMouseMove}
                  on:mouseleave={handleTimelineMouseLeave}
                  role="slider"
                  tabindex="0"
                  aria-valuemin="0"
                  aria-valuemax={duration}
                  aria-valuenow={trimStart}
                >
                  {#if isGeneratingTimeline}
                    <div
                      class="pointer-events-none absolute inset-0 flex h-full w-full items-center justify-center bg-gray-400"
                    >
                      <div class="h-full w-full overflow-hidden">
                        <div
                          class="h-full bg-black/30 transition-all duration-300 ease-in-out"
                          style="width: {generateTimelineProgress}%"
                        />
                      </div>
                      <div class="absolute inset-0 flex h-full w-full items-center justify-center gap-x-2">
                        {#if !errorMessage}
                          <Icon name="Spinner" className="text-sermon-base-50  w-4 h-4 animate-spin" />
                        {/if}
                        <span class="text-sermon-base-50">{errorMessage || 'Generating preview...'}</span>
                      </div>
                    </div>
                  {/if}
                  {#if timeline.length > 0}
                    <!-- Preview Images -->
                    <div class="absolute inset-0 flex h-full w-full overflow-hidden">
                      {#each timeline as frame}
                        <div
                          class="h-full flex-1"
                          style="background-image: url({frame.url}); background-size: cover; background-position: center;"
                        />
                      {/each}
                    </div>
                  {/if}
                  <!-- Progress Bar -->
                  <div class="absolute inset-0 border-y-4 border-gray-400" />
                  <div
                    class="absolute h-full cursor-pointer border-y-4 border-sermon-pink bg-sermon-pink-500/30"
                    style="left: {(trimStart / duration) * 100}%; right: {100 - (trimEnd / duration) * 100}%"
                  />

                  <!-- Current Time Indicator -->
                  {#if !isDraggingStart && !isDraggingEnd}
                    <div
                      id="video-timeline-seeker"
                      class="absolute bottom-0 h-[73px] w-1 cursor-ew-resize bg-sermon-base-600 before:absolute before:left-1/2 before:top-[-6px] before:h-3 before:w-3 before:-translate-x-1/2 before:bg-sermon-base-600 before:content-[''] before:[clip-path:polygon(50%_100%,0%_0%,100%_0%)]"
                      style="left: {(currentTime / duration) * 100}%"
                      on:mousedown|preventDefault={e => handleDragStart(e, 'time')}
                    />
                  {/if}

                  <!-- Trim Handles -->
                  <div
                    class="absolute flex h-full w-4 -translate-x-full cursor-ew-resize items-center justify-center bg-sermon-pink"
                    style="left: {(trimStart / duration) * 100}%"
                    on:mousedown|preventDefault={e => handleDragStart(e, 'start')}
                  >
                    <svg class="h-4 w-2" viewBox="0 0 4 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2 1V11" stroke="white" stroke-width="1" stroke-linecap="round" />
                    </svg>
                  </div>
                  <div
                    class="absolute flex h-full w-4 cursor-ew-resize items-center justify-center rounded-r-xl bg-sermon-pink"
                    style="left: {(trimEnd / duration) * 100}%"
                    on:mousedown|preventDefault={e => handleDragStart(e, 'end')}
                  >
                    <svg class="h-4 w-2" viewBox="0 0 4 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2 1V11" stroke="white" stroke-width="1" stroke-linecap="round" />
                    </svg>
                  </div>
                </div>

                <!-- Right Filler -->
                <div
                  class={`bg-gray-4 absolute flex h-full w-4 rounded-r-xl border-gray-500 bg-gray-400`}
                  style="left: 99.7%"
                />
              </div>
            </div>

            <!-- Time Inputs -->
            <div class="mt-3 flex w-full items-center justify-center gap-x-2">
              <TimeInput
                label="Start time:"
                hours={startTime.hours}
                minutes={startTime.minutes}
                seconds={startTime.seconds}
                options={getTimeConstraints(true, startTime, endTime, durationTime)}
                on:input={e => handleTimeInput(e, true)}
              />
              <TimeInput
                label="End time:"
                hours={endTime.hours}
                minutes={endTime.minutes}
                seconds={endTime.seconds}
                options={getTimeConstraints(false, startTime, endTime, durationTime)}
                on:input={e => handleTimeInput(e, false)}
              />
            </div>
          </div>
        {/if}
      </div>

      <div class="mt-4 flex justify-end gap-4">
        <button type="submit" data-action="upload" class={`btn  ${isTrimModified ? 'btn-secondary' : 'btn-primary'}`}>
          Upload Without Trimming
        </button>
        {#if isTrimModified}
          <button type="submit" data-action="trim" class="btn btn-primary">Trim & Upload</button>
        {/if}
      </div>
    </form>
  </div>
</Dialog>

<svelte:window on:mousemove={handleWindowMouseMove} on:mouseup={handleDragEnd} on:mouseleave={handleDragEnd} />
